import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// Version constants - update these when terms change
export const CURRENT_GENERAL_TERMS_VERSION = '1.1';
export const CURRENT_SELLER_TERMS_VERSION = '1.0';

/**
 * Shared, cached terms-acceptance status. Previously every component mount
 * (and every auth token refresh) re-queried the profile — hundreds of
 * thousands of calls. Now one cached lookup per user per session; call
 * `refreshTermsStatus()` after the user accepts terms.
 */
export function useTermsAcceptance() {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['terms-acceptance', userId],
    enabled: !!userId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('general_terms_accepted_at, general_terms_version, terms_accepted_at, terms_version')
          .eq('user_id', userId!)
          .single();
        if (error) throw error;
        return {
          general: !!(data?.general_terms_accepted_at && data?.general_terms_version === CURRENT_GENERAL_TERMS_VERSION),
          seller: !!(data?.terms_accepted_at && data?.terms_version === CURRENT_SELLER_TERMS_VERSION),
        };
      } catch (error) {
        console.error('Error checking terms acceptance:', error);
        return { general: false, seller: false };
      }
    },
  });

  const refreshTermsStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['terms-acceptance', userId] });
  }, [queryClient, userId]);

  const hasAcceptedGeneralTerms = userId ? (query.data?.general ?? null) : null;
  const hasAcceptedSellerTerms = userId ? (query.data?.seller ?? null) : null;

  return {
    hasAcceptedGeneralTerms,
    hasAcceptedSellerTerms,
    // Legacy alias for backwards compatibility
    hasAcceptedTerms: hasAcceptedSellerTerms,
    isLoading: !!userId && query.isLoading,
    refreshTermsStatus,
  };
}
